import { useMemo, useState } from 'react';
import { CalendarEvent } from 'interfaces/calendar.interface.js';
import { ErrorCode } from 'components/other/_ErrorIcons';
import { dayjs } from '@umanforge/common';
import { GlobalService } from 'services/global.service';

export const useChronogramEvent = (
  event: CalendarEvent,
  columnRange: { start: number, end: number },
  celSize: number,
  lineId: string,
  date: dayjs.Dayjs,
  onDragEnd: ( ) => void,
  onDragStart: ( event: CalendarEvent ) => void,
  stackable: boolean = true
) => {

  const isFullDayEvent = ( _event: CalendarEvent ): boolean => {
    return (
      dayjs( _event.start ).format( 'YYYY-MM-DD' ) == dayjs( _event.end ).format( 'YYYY-MM-DD' ) &&
      dayjs( _event.start ).format( 'HH:mm' ) === '00:00' &&
      dayjs( _event.end ).format( 'HH:mm' ) === '23:59'
    );
  };

  const overflowHour = false;

  const { isPastEvent } = useMemo( () => {
    return {
      isPastEvent: dayjs( event.start ) < dayjs()
    };
  }, [ event ] );

  const [ dragged, setDragged ] = useState( false );

  const handleDragStart = ( ev: CalendarEvent ) => {
    setDragged( true );
    onDragStart( ev );
  };
  const handleDragEnd = ( ) => {
    setDragged( false );
    onDragEnd( );
  };

  const { colStart, colEnd, startoffset, maxWidth, width, errors } = useMemo( () => {
    const start = dayjs( event.task?.dateStart );
    const end = dayjs( event.task?.dateEnd );

    const hourCells = Math.floor( 60 / celSize );
    const hourOffset = columnRange.start - ( 2 / hourCells );
    const _maxWidth = 12 / hourCells;

    const startIsOnSameDay = start.startOf( 'day' ).isSame( date.startOf( 'day' ) );
    const endIsOnSameDay = end.startOf( 'day' ).isSame( date.startOf( 'day' ) );

    let _colStart = 0;
    let _colEnd = 0;
    let _startoffset = 0;
    let endoffset = 0;


    if ( startIsOnSameDay ) {
      const startMinute = start.minute();
      const startMinuteCol = Math.round( startMinute / celSize );
      _colStart = Math.floor( ( ( start.hour() - hourOffset ) * hourCells ) + startMinuteCol - 1 + 0.0001 );
      _startoffset = startMinute - startMinuteCol * celSize;
      _colStart = Math.max( _colStart, 1 );
    }
    else {
      _colStart = 1;
    }

    if ( endIsOnSameDay ) {
      const endMinute = end.minute();
      const endMinutesCol = Math.floor( endMinute / celSize );

      _colEnd = Math.floor( ( ( end.hour() - hourOffset ) * hourCells ) + endMinutesCol - 1 + 0.0001 );
      endoffset = ( endMinute - endMinutesCol * celSize ) - _startoffset;
      _colEnd = Math.min( _colEnd, GlobalService.hoursLenghtByRange( columnRange, 1 / hourCells ) );
    }
    else {
      _colEnd = GlobalService.hoursLenghtByRange( columnRange, 1 / hourCells ) + 1;
    }

    const _errors: ErrorCode[] = [];
    if ( event.understaffed ) { _errors.push( 'understaffed' ); }
    if ( event.task?.location == null ) { _errors.push( 'no_location' ); }
    if ( event.title == null ) { _errors.push( 'error' ); }
    if ( stackable && event.stacked && lineId !== 'not_assigned' ) { _errors.push( 'stacked' ); }

    return {
      colStart: _colStart,
      colEnd: _colEnd,
      startoffset: _startoffset,
      endoffset: endoffset,
      maxWidth: _maxWidth,
      width: ( 100 + ( endoffset * 100 ) / ( celSize * ( _colEnd - _colStart ) ) ),
      errors: _errors
    };
  }, [ celSize, columnRange, event ] );

  return {
    colStart, colEnd, startoffset, maxWidth, width, errors,
    isPastEvent,
    dragged,
    handleDragStart,
    handleDragEnd,
    overflowHour,
    isFullDayEvent
  };
};